import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { X, Check, Upload, Loader2, LogOut, Shield } from "lucide-react";
import SiteShell from "../components/SiteShell";
import HoverChat from "../components/HoverChat";
import Action from "../components/Action";
import {
  redeemDataRoomToken,
  getDataRoomSession,
  logoutDataRoom,
  submitPartnershipRfp,
} from "../lib/api";
import { Field, inputStyle, primaryButtonStyle } from "./audit/StepEnergy";

/**
 * `/data-room` — the gated funder cockpit.
 *
 * Two modes:
 *   - no valid cookie → token-paste screen (or auto-consume ?token=... from URL)
 *   - valid cookie    → the cockpit (metrics, unit econ, structure, team, action)
 */
export default function DataRoom() {
  const [session, setSession] = useState(undefined); // undefined = loading
  const [redeemBusy, setRedeemBusy] = useState(false);
  const [redeemErr, setRedeemErr] = useState("");
  const [tokenInput, setTokenInput] = useState("");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlToken = searchParams.get("token");

  // On mount: try to auto-redeem ?token=..., otherwise check for existing cookie.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (urlToken) {
          setRedeemBusy(true);
          try {
            const data = await redeemDataRoomToken(urlToken);
            if (!alive) return;
            // Strip the token from the address bar BEFORE the setSession
            // re-render, so the router doesn't race the history push.
            window.history.replaceState({}, "", "/data-room");
            setSession({ authorised: true, org: data.org, ticket_band: data.ticket_band });
            return;
          } catch (ex) {
            if (!alive) return;
            setRedeemErr(ex?.response?.data?.detail || "That token isn't valid or has expired.");
            // Move the user off the /access URL so a page refresh doesn't retry a bad token.
            window.history.replaceState({}, "", "/data-room");
            // Fall through to gate rendering by resolving the session to null.
            setSession(null);
          } finally {
            if (alive) setRedeemBusy(false);
          }
        }
        const s = await getDataRoomSession();
        if (alive) setSession(s);
      } catch {
        if (alive) setSession(null);
      }
    })();
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlToken]);

  const doLogout = async () => {
    await logoutDataRoom();
    setSession(null);
  };

  const submitToken = async (e) => {
    e.preventDefault();
    if (!tokenInput.trim() || redeemBusy) return;
    setRedeemBusy(true);
    setRedeemErr("");
    try {
      const data = await redeemDataRoomToken(tokenInput.trim());
      setSession({ authorised: true, org: data.org, ticket_band: data.ticket_band });
    } catch (ex) {
      setRedeemErr(ex?.response?.data?.detail || "That token isn't valid or has expired.");
    } finally {
      setRedeemBusy(false);
    }
  };

  return (
    <SiteShell>
      <section
        style={{
          maxWidth: 1080,
          margin: "0 auto",
          padding: "clamp(52px, 8vw, 100px) clamp(20px, 5vw, 60px) clamp(50px, 7vw, 90px)",
        }}
        data-testid="data-room-page"
      >
        {session === undefined ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--text-dim)" }}>
            <Loader2 size={16} className="animate-spin" /> Checking your access…
          </div>
        ) : !session?.authorised ? (
          <div data-testid="data-room-gate">
            <div className="gg-eyebrow" style={{ marginBottom: 10 }}>Data room · By invitation</div>
            <h1 className="gg-hero-title" style={{ fontSize: "clamp(30px, 4vw, 50px)", marginBottom: 18, maxWidth: "20ch" }}>
              This room is by invitation.
            </h1>
            <p style={{ color: "var(--text-dim)", fontSize: 16.5, lineHeight: 1.7, maxWidth: 620 }}>
              If our team has issued you a magic link, paste your access token
              below. Otherwise you can{" "}
              <Action route="request-data-room" source="data-room-gate" onClick={() => navigate("/partner")}>
                request access from the funder gateway
              </Action>
              .
            </p>

            <form onSubmit={submitToken} style={{ marginTop: 34, maxWidth: 620 }}>
              <Field label="Access token">
                <input
                  type="text" value={tokenInput}
                  onChange={(e) => setTokenInput(e.target.value)}
                  placeholder="Paste the token from your email"
                  data-testid="data-room-token-input"
                  style={{ ...inputStyle, fontFamily: "monospace", fontSize: 13.5 }}
                />
              </Field>
              {redeemErr && (
                <p data-testid="data-room-token-error" style={{ marginTop: 10, color: "#ff8a8a", fontSize: 13 }}>{redeemErr}</p>
              )}
              <div style={{ marginTop: 18 }}>
                <button type="submit" disabled={!tokenInput.trim() || redeemBusy} data-testid="data-room-token-submit" style={primaryButtonStyle(!tokenInput.trim() || redeemBusy)}>
                  {redeemBusy ? <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Loader2 size={14} className="animate-spin" /> Redeeming…</span> : "Enter the data room"}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <Cockpit session={session} onLogout={doLogout} />
        )}
      </section>
    </SiteShell>
  );
}

// ============ Cockpit (gated content) ============
function Cockpit({ session, onLogout }) {
  const [prfpOpen, setPrfpOpen] = useState(false);

  return (
    <div data-testid="data-room-cockpit">
      <div style={{
        display: "flex", flexWrap: "wrap", justifyContent: "space-between",
        alignItems: "flex-end", gap: 16, marginBottom: 30,
      }}>
        <div>
          <div className="gg-eyebrow" style={{ marginBottom: 8 }}>
            <Shield size={12} style={{ display: "inline", marginRight: 6, marginBottom: -1 }} />
            Data room · authorised
          </div>
          <h1 className="gg-hero-title" style={{ fontSize: "clamp(28px, 3.6vw, 44px)", marginBottom: 8, maxWidth: "22ch" }}>
            The plant, in the round.
          </h1>
          <p style={{ color: "var(--text-dim)", fontSize: 14.5 }}>
            Welcome, <strong style={{ color: "var(--gold-soft)" }} data-testid="cockpit-org">{session.org}</strong>{" "}
            · ticket band {session.ticket_band}. Contents as of Q4 2025 – Q1 2026.
          </p>
        </div>
        <button
          type="button" onClick={onLogout} data-testid="data-room-logout"
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "8px 14px", border: "1px solid var(--line)", borderRadius: 999,
            background: "transparent", color: "var(--text-dim)", cursor: "pointer", fontSize: 13,
          }}
        >
          <LogOut size={13} /> Sign out
        </button>
      </div>

      {/* 1. Live metrics band (mirrors /partner) */}
      <SectionTitle>Live metrics</SectionTitle>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 14 }}>
        {[
          ["Allocation ticket", "₹850 Cr"],
          ["Plant capacity", "720 MWh/yr"],
          ["Site", "SIPCOT, Coimbatore"],
          ["Structure", "BOOT + SPV"],
          ["Milestones", "3-tranche schedule"],
        ].map(([k, v]) => (
          <div key={k} className="gg-glass" style={{ padding: 14 }}>
            <div style={{ fontSize: 10.5, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--text-mute)" }}>{k}</div>
            <div style={{ fontFamily: "Fraunces, Georgia, serif", fontSize: 20, color: "var(--paper)", marginTop: 4 }}>{v}</div>
          </div>
        ))}
      </div>

      {/* 2. Unit economics */}
      <SectionTitle>Unit economics</SectionTitle>
      <div className="gg-glass" style={{ padding: "18px 20px", overflowX: "auto" }} data-testid="cockpit-unit-econ">
        <table style={{ width: "100%", borderCollapse: "collapse", color: "var(--text-cloud)", fontSize: 14 }}>
          <thead>
            <tr style={{ color: "var(--text-mute)", fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase" }}>
              {["Metric", "Grid baseline", "Our plant (SPV)"].map((h) => (
                <th key={h} style={{ padding: "10px 12px", textAlign: "left", borderBottom: "1px solid var(--line)" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[
              ["Landed ₹/kWh (industry)", "₹8 – 12", <>₹4 – 5.50 under <HoverChat term="gcpp">GCPP</HoverChat></>],
              ["LFP cell cost (2024 reference)", "> $110 / kWh", "< $90 / kWh at cell-level"],
              ["Warranted cycles / life", "—", "3,000 – 6,000 · 10 – 15 yrs"],
              ["Plant CUF (design)", "—", "17 % solar · 300 BESS working days"],
              ["Payback (typical customer SB)", "—", "3 – 6 years indicative"],
            ].map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td key={j} style={{ padding: "10px 12px", borderBottom: "1px solid var(--line)", verticalAlign: "top" }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <p style={{ marginTop: 10, color: "var(--text-mute)", fontSize: 12, lineHeight: 1.55 }}>
          Sourced from the SPV pitch text. Deeper IRR / LCOE ranges are provided
          upon Request for Partnership under NDA.
        </p>
      </div>

      {/* 3. Structure */}
      <SectionTitle>Structure · BOOT + SPV</SectionTitle>
      <div className="gg-glass" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
        <pre style={{
          margin: 0, color: "var(--text-cloud)", fontFamily: "monospace",
          fontSize: 12.5, lineHeight: 1.55, overflowX: "auto",
        }}>{`   ┌─────────────────────────┐          ┌─────────────────────────┐
   │  Green Gold Energy Pvt. │          │       One funder /       │
   │       Ltd. (parent)     │          │   strategic partner      │
   │   100% privately held   │          │                          │
   └────────────┬────────────┘          └─────────────┬────────────┘
                │                                     │
                │ operator + IP + team                │ tranche capital
                ▼                                     ▼
             ┌───────────────────────────────────────────┐
             │        Plant SPV — BOOT vehicle           │
             │   720 MWh/yr · SIPCOT, Coimbatore         │
             │   Co-owned. Build → Operate → Own → Transfer │
             └───────────────────────────────────────────┘`}</pre>
        <p style={{ color: "var(--text-dim)", fontSize: 14.5, lineHeight: 1.7 }}>
          Tranche schedule: (1) cell-line commissioned, (2) first module
          shipments, (3) first MWh dispatched under GCPP / CBAM-grade offtake.
          Exit pathway: buyback at pre-agreed multiple at end of BOOT term,
          or continued co-ownership. Parent equity is not available under any
          scenario.
        </p>
      </div>

      {/* 4. Team & R&D */}
      <SectionTitle>Team &amp; R&amp;D</SectionTitle>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }} data-testid="cockpit-team-grid">
        {[
          "Founder & Chief Visionary",
          "Co-Founder & Strategic Partner",
          "Chief Strategy Officer",
          "Director of Battery R&D",
          "3 × IIT Professors — cell chemistry / BMS / battery engineering",
          "4 × IIT Engineers",
          "15-person in-house R&D bench",
          "60+ execution team",
        ].map((role, i) => (
          <div key={i} className="gg-glass" style={{ padding: 14, fontSize: 14, lineHeight: 1.5 }}>
            <div style={{ fontFamily: "Fraunces, Georgia, serif", color: "var(--paper)" }}>{role}</div>
            <div style={{ color: "var(--text-mute)", fontSize: 11.5, marginTop: 4, letterSpacing: "0.12em", textTransform: "uppercase" }}>
              Bio provided upon Request for Partnership
            </div>
          </div>
        ))}
      </div>

      {/* 5. Subsidy / land */}
      <SectionTitle>Land &amp; subsidy status</SectionTitle>
      <div className="gg-glass" style={{ padding: 18, fontSize: 14.5, lineHeight: 1.7, color: "var(--text-dim)" }}>
        Tamil Nadu government support is <em>in process</em> — SEZ instruments
        and SIPCOT land allocation applications are filed and referenced in
        the accompanying legal docs (provided upon Request for Partnership).
        No figures on subsidy quantum are surfaced here; those are covered
        under NDA in the partnership packet.
      </div>

      {/* 6. Supply-node positioning */}
      <SectionTitle>Supply-node positioning</SectionTitle>
      <div className="gg-glass" style={{ padding: 18, fontSize: 14.5, lineHeight: 1.7, color: "var(--text-dim)" }}>
        A non-Chinese, Indo-Pacific cell-to-pack chain — LFP now, sodium-ion
        next on the same machinery, anode from Tamil Nadu / Kerala coconut
        shells. This is the China+1 axis for Indian and allied energy capital.
      </div>

      {/* 7. Action panel */}
      <div style={{
        marginTop: 44, padding: 26, borderRadius: "var(--radius-lg)",
        background: "linear-gradient(180deg, rgba(244,169,31,0.10), rgba(244,169,31,0.02))",
        border: "1px solid rgba(244,169,31,0.35)",
      }} data-testid="cockpit-action-panel">
        <div className="gg-eyebrow" style={{ marginBottom: 8 }}>The next step</div>
        <h2 className="gg-h1" style={{ fontSize: "clamp(24px, 2.5vw, 32px)", marginBottom: 10 }}>
          Submit a Request for Partnership.
        </h2>
        <p style={{ color: "var(--text-cloud)", fontSize: 15.5, lineHeight: 1.7, maxWidth: 720 }}>
          When you're ready to formalise,{" "}
          <Action route="partnership-rfp" source="cockpit" onClick={() => setPrfpOpen(true)}>
            submit your term sheet or our structured form
          </Action>
          . If you'd like to walk the plant first, you can also{" "}
          <Action route="book-tour" source="cockpit">schedule a site visit</Action>.
        </p>
      </div>

      {prfpOpen && <PartnershipDrawer session={session} onClose={() => setPrfpOpen(false)} />}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 className="gg-h2" style={{
      marginTop: 42, marginBottom: 14, fontSize: "clamp(18px, 2vw, 22px)",
      color: "var(--paper)", fontFamily: "Fraunces, Georgia, serif",
    }}>{children}</h2>
  );
}

// ============ Partnership RFP drawer ============
function PartnershipDrawer({ session, onClose }) {
  const [form, setForm] = useState({
    ticket_size_inr: "",
    tranche_structure: "",
    milestone_alignment: "",
    exit_terms: "",
  });
  const [termSheet, setTermSheet] = useState(null);
  const [fileErr, setFileErr] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(null);

  const canSubmit = ack && !busy;

  const onFile = (e) => {
    const f = e.target.files?.[0];
    setFileErr("");
    if (!f) { setTermSheet(null); return; }
    const ext = f.name.slice(f.name.lastIndexOf(".")).toLowerCase();
    if (![".pdf", ".docx"].includes(ext)) { setFileErr("Only .pdf or .docx accepted for term sheets."); return; }
    if (f.size > 25 * 1024 * 1024) { setFileErr(`"${f.name}" exceeds the 25 MB limit.`); return; }
    setTermSheet(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true); setErr("");
    try {
      const data = await submitPartnershipRfp({
        fields: { ...form, spv_acknowledged: "true" },
        termSheet,
      });
      setOk(data);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || ex?.message || "Submission failed.");
    } finally {
      setBusy(false);
    }
  };

  if (ok) {
    return (
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        data-testid="prfp-confirmation"
        style={{
          marginTop: 30, padding: 26, borderRadius: "var(--radius-lg)",
          background: "linear-gradient(180deg, rgba(31,170,89,0.14), rgba(31,170,89,0.02))",
          border: "1px solid rgba(31,170,89,0.4)",
        }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
          <Check size={18} color="var(--green-bright)" />
          <span className="gg-eyebrow" style={{ color: "var(--green-bright)", margin: 0 }}>Partnership request received</span>
        </div>
        <h3 className="gg-h2">{ok.message}</h3>
        <p style={{ marginTop: 12, fontFamily: "Fraunces, Georgia, serif", fontSize: 20, color: "var(--gold-soft)" }}>
          Reference: <span data-testid="prfp-ref-id">{ok.ref_id}</span>
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} transition={{ duration: 0.35 }}
      data-testid="prfp-drawer" style={{ marginTop: 30, overflow: "hidden" }}>
      <div style={{
        padding: "24px 26px 28px", borderRadius: "var(--radius-lg)",
        background: "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.01))",
        border: "1px solid var(--line-strong)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 18 }}>
          <div>
            <div className="gg-eyebrow" style={{ marginBottom: 4 }}>Request for Partnership</div>
            <h3 className="gg-h2">Your terms — or ours to work from.</h3>
            <p style={{ color: "var(--text-dim)", fontSize: 14, marginTop: 6, lineHeight: 1.6, maxWidth: 620 }}>
              Upload your own term sheet, use the structured form below, or
              both. All submissions go to the SPV workstream lead.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="prfp-close"
            style={{
              background: "transparent", border: "1px solid var(--line)", borderRadius: 8,
              width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center",
              color: "var(--text-dim)", cursor: "pointer", flexShrink: 0,
            }}>
            <X size={14} />
          </button>
        </div>

        <form onSubmit={submit}>
          <div style={{ marginBottom: 18 }}>
            <label htmlFor="prfp-file" style={{
              display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 14px",
              borderRadius: 10, border: "1px dashed var(--line-strong)", cursor: "pointer",
              color: "var(--text-dim)", fontSize: 14,
            }}>
              <Upload size={14} /> Upload your term sheet (.pdf / .docx ≤ 25 MB, optional)
            </label>
            <input id="prfp-file" type="file" accept=".pdf,.docx" onChange={onFile} data-testid="prfp-file" style={{ display: "none" }} />
            {termSheet && <div data-testid="prfp-file-name" style={{ marginTop: 8, color: "var(--text-cloud)", fontSize: 13 }}>{termSheet.name}</div>}
            {fileErr && <p data-testid="prfp-file-error" style={{ marginTop: 8, color: "#ff8a8a", fontSize: 13 }}>{fileErr}</p>}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
            <Field label="Ticket size (₹)"><input type="text" value={form.ticket_size_inr} onChange={(e) => setForm({ ...form, ticket_size_inr: e.target.value })} data-testid="prfp-ticket" style={inputStyle} placeholder="e.g. ₹150 Cr" /></Field>
          </div>
          {[
            ["tranche_structure", "Tranche structure", "prfp-tranche", "How you'd like tranches keyed to milestones."],
            ["milestone_alignment", "Milestone alignment", "prfp-milestone", "Which construction milestones matter most to your workstream."],
            ["exit_terms", "Preferred BOOT / exit terms", "prfp-exit", "Buyback multiple, term length, continuation options."],
          ].map(([k, label, tid, ph]) => (
            <div key={k} style={{ marginTop: 14 }}>
              <Field label={label} hint={ph}>
                <textarea rows={3} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  data-testid={tid} style={{ ...inputStyle, resize: "vertical", minHeight: 72, fontFamily: "inherit" }} />
              </Field>
            </div>
          ))}

          <label style={{
            marginTop: 22, display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer",
            padding: 14, borderRadius: 10, background: ack ? "rgba(31,170,89,0.10)" : "rgba(255,255,255,0.03)",
            border: `1px solid ${ack ? "rgba(31,170,89,0.4)" : "var(--line)"}`,
          }}>
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="prfp-ack" style={{ marginTop: 3, accentColor: "var(--green-bright)" }} />
            <span style={{ color: "var(--text-cloud)", fontSize: 14.5, lineHeight: 1.55 }}>
              <strong>I understand this allocation is SPV-only. Parent-company equity is not available.</strong>
            </span>
          </label>

          {err && <p data-testid="prfp-submit-error" style={{ marginTop: 12, color: "#ff8a8a", fontSize: 13 }}>{err}</p>}

          <div style={{ marginTop: 22, display: "flex", justifyContent: "flex-end" }}>
            <button type="submit" disabled={!canSubmit} data-testid="prfp-submit" style={primaryButtonStyle(!canSubmit)}>
              {busy ? <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Loader2 size={14} className="animate-spin" /> Submitting…</span> : "Submit Request for Partnership"}
            </button>
          </div>
        </form>
      </div>
    </motion.div>
  );
}
